// SPDX-License-Identifier: GPL-3.0-or-later
// The featured-turn WebSocket (WS /api/featured/ws).
//
// Protocol (contracts.ts, src/worker/featured-msg.ts):
// 1. After the socket opens, the first frame is `{type:"auth", ticket}`. The ticket is never in the URL.
// 2. The Worker answers `ready`. Then the client sends `state`, `event` and `game_over` frames.
// 3. `seq` rises with each frame. `game_ms` is the game time. The score only goes up.
// 4. At most STATE_MSG_MAX_PER_SEC state and event frames in a second (the Worker drops extra frames).
//    This client keeps a gap of FRAME_GAP_MS between two frames, so it sends 8 frames a second at most.
// The stream never reads a game rule. It maps the engine status to the wire status and nothing else.

import {
  type EngineEvent,
  type FeaturedClientMessage,
  type FeaturedEventWire,
  type FeaturedServerMessage,
  type FeaturedStatus,
  type GameStatus,
  ROUTES,
  STATE_MSG_MAX_PER_SEC,
  WS_MSG_MAX_BYTES,
} from "../src/shared/contracts.ts";

/** Time between two frames. 125 ms = 8 frames a second, under the limit of 10. */
export const FRAME_GAP_MS = Math.ceil(1000 / STATE_MSG_MAX_PER_SEC) + 25;
/** A state frame goes out at least this often, also when nothing changed. */
export const HEARTBEAT_MS = 1000;
/** While the game is paused, the same state frame goes out this often (follow-up F11). */
export const PAUSED_HEARTBEAT_MS = 5000;
/** At most this many event frames wait. The oldest one is dropped first. */
const MAX_PENDING_EVENTS = 16;

/** The part of a WebSocket that the stream uses. */
export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { readonly data: unknown }) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

/** WebSocket ready state of an open socket. */
const SOCKET_OPEN = 1;

/** Events from the stream to the page. */
export interface StreamHandlers {
  /** The Worker accepted the ticket. The page starts the game now. */
  onReady(msg: Extract<FeaturedServerMessage, { type: "ready" }>): void;
  onAck(msg: Extract<FeaturedServerMessage, { type: "ack" }>): void;
  /** The Worker ended the turn. The stream is closed. */
  onEnd(msg: Extract<FeaturedServerMessage, { type: "end" }>): void;
  onError(msg: Extract<FeaturedServerMessage, { type: "error" }>): void;
  /** The socket closed with no `end` message. */
  onLost(): void;
}

/** The stream. */
export interface FeaturedStream {
  readonly state: "connecting" | "authenticating" | "ready" | "closed";
  /** The last `seq` that went out. Pass it to the next stream after a reconnect. */
  readonly lastSeq: number;
  /** Queue an engine event. Only the events of FeaturedEventWire go out. */
  queueEvent(ev: EngineEvent, gameMs: number): void;
  /** Send the final score. It goes out at the next free slot. */
  finish(finalScore: number, gameMs: number): void;
  /**
   * Send a state frame when the gap is over and something changed (or the heartbeat is due).
   * While `paused` is true, the same status goes out every PAUSED_HEARTBEAT_MS.
   */
  pump(nowMs: number, status: GameStatus, paused?: boolean): void;
  close(): void;
}

/** Whole number inside a range. */
function whole(value: number, min: number, max: number): number {
  return Number.isFinite(value) ? Math.min(max, Math.max(min, Math.floor(value))) : min;
}

/** Map the engine status to the wire status. The Worker checks every field again. */
export function toFeaturedStatus(status: GameStatus): FeaturedStatus {
  return {
    phase: status.phase,
    score: whole(status.score, 0, Number.MAX_SAFE_INTEGER),
    ball_number: whole(status.ballNumber, 0, 10),
    balls_per_game: whole(status.ballsPerGame, 1, 10),
    balls_in_play: whole(status.ballsInPlay, 0, 10),
    multiplier: whole(status.multiplier, 1, 10),
    active_modes: status.activeModes.slice(0, 8),
  };
}

/** Map an engine event to the wire event. Events that stay local give null. */
export function toWireEvent(ev: EngineEvent): FeaturedEventWire | null {
  switch (ev.type) {
    case "mode_start":
      return { type: "mode_start", mode_id: ev.modeId, label: ev.label };
    case "mode_end":
      return { type: "mode_end", mode_id: ev.modeId, completed: ev.completed };
    case "multiball":
      return {
        type: "multiball",
        balls_in_play: whole(ev.ballsInPlay, 0, 10),
        active: ev.active,
      };
    case "ball_lost":
      return { type: "ball_lost", ball_number: whole(ev.ballNumber, 0, 10) };
    case "ball_saved":
      return { type: "ball_saved", ball_number: whole(ev.ballNumber, 0, 10) };
    default:
      return null;
  }
}

/** A key of the status fields that change. The heartbeat covers the rest. */
function statusKey(s: FeaturedStatus): string {
  return `${s.phase}|${s.score}|${s.ball_number}|${s.balls_in_play}|${s.multiplier}|${s.active_modes.join(",")}`;
}

type ErrorCode = Extract<FeaturedServerMessage, { type: "error" }>["error"];

/** Parse one server frame. Anything that is not a known frame gives null. */
export function parseServerMessage(data: unknown): FeaturedServerMessage | null {
  if (typeof data !== "string") return null;
  let value: unknown;
  try {
    value = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const parse = typeof v.type === "string" && Object.hasOwn(FRAME_PARSERS, v.type);
  return parse ? (FRAME_PARSERS[v.type as string]?.(v) ?? null) : null;
}

type Fields = Record<string, unknown>;
type FrameParser = (v: Fields) => FeaturedServerMessage | null;

const END_REASONS: ReadonlySet<unknown> = new Set([
  "game_over",
  "time_limit",
  "operator",
  "disconnect",
]);

function parseReady(v: Fields): FeaturedServerMessage | null {
  return typeof v.turn_id === "string" && typeof v.ends_at === "string"
    ? { type: "ready", turn_id: v.turn_id, ends_at: v.ends_at }
    : null;
}

function parseAck(v: Fields): FeaturedServerMessage | null {
  const shaped =
    typeof v.seq === "number" &&
    typeof v.accepted_score === "number" &&
    typeof v.clamped === "boolean";
  return shaped
    ? {
        type: "ack",
        seq: v.seq as number,
        accepted_score: v.accepted_score as number,
        clamped: v.clamped as boolean,
      }
    : null;
}

function parseEnd(v: Fields): FeaturedServerMessage | null {
  if (typeof v.final_score !== "number" || !END_REASONS.has(v.reason)) return null;
  return {
    type: "end",
    reason: v.reason as Extract<FeaturedServerMessage, { type: "end" }>["reason"],
    final_score: v.final_score,
  };
}

function parseError(v: Fields): FeaturedServerMessage | null {
  return typeof v.error === "string" && typeof v.message === "string"
    ? { type: "error", error: v.error as ErrorCode, message: v.message }
    : null;
}

/** One parser for each known frame type. */
const FRAME_PARSERS: Readonly<Record<string, FrameParser>> = {
  ready: parseReady,
  ack: parseAck,
  end: parseEnd,
  error: parseError,
  pong: () => ({ type: "pong" }),
};

/** Options of a stream. */
export interface StreamOptions {
  /** The first `seq` minus one. After a reconnect, pass the `lastSeq` of the old stream. */
  readonly startSeq?: number;
}

/** The state of one stream. The functions below change it. */
interface StreamCore {
  readonly socket: SocketLike;
  readonly ticket: string;
  readonly handlers: StreamHandlers;
  state: FeaturedStream["state"];
  seq: number;
  lastSend: number;
  lastKey: string;
  readonly pendingEvents: Array<{ event: FeaturedEventWire; gameMs: number }>;
  final: { score: number; gameMs: number } | null;
  finalSent: boolean;
}

function sendFrame(core: StreamCore, message: FeaturedClientMessage): void {
  const text = JSON.stringify(message);
  // The Worker closes the socket for a frame over WS_MSG_MAX_BYTES. Ours are far smaller.
  if (text.length > WS_MSG_MAX_BYTES || core.socket.readyState !== SOCKET_OPEN) return;
  core.socket.send(text);
}

function authenticate(core: StreamCore): void {
  core.state = "authenticating";
  sendFrame(core, { type: "auth", ticket: core.ticket });
}

function nextSeq(core: StreamCore): number {
  core.seq += 1;
  return core.seq;
}

/** Handle one parsed server frame. */
function onServerFrame(core: StreamCore, msg: FeaturedServerMessage): void {
  switch (msg.type) {
    case "ready":
      if (core.state === "authenticating") core.state = "ready";
      core.handlers.onReady(msg);
      return;
    case "ack":
      core.handlers.onAck(msg);
      return;
    case "end":
      core.state = "closed";
      core.handlers.onEnd(msg);
      core.socket.close(1000, "turn ended");
      return;
    case "error":
      core.handlers.onError(msg);
      return;
    case "pong":
      return;
  }
}

function onSocketClose(core: StreamCore): void {
  const wasClosed = core.state === "closed";
  core.state = "closed";
  if (!wasClosed) core.handlers.onLost();
}

function bindSocket(core: StreamCore): void {
  const { socket } = core;
  socket.onopen = () => authenticate(core);
  // A socket that is open already (a fake in a test) authenticates now.
  if (socket.readyState === SOCKET_OPEN) authenticate(core);
  socket.onmessage = (ev) => {
    const msg = parseServerMessage(ev.data);
    if (msg !== null) onServerFrame(core, msg);
  };
  socket.onclose = () => onSocketClose(core);
  socket.onerror = () => {
    // The close event follows. Nothing to do here.
  };
}

function queueStreamEvent(core: StreamCore, ev: EngineEvent, gameMs: number): void {
  const event = toWireEvent(ev);
  if (event === null || core.state === "closed") return;
  if (core.pendingEvents.length >= MAX_PENDING_EVENTS) core.pendingEvents.shift();
  core.pendingEvents.push({ event, gameMs: Math.max(0, gameMs) });
}

function setFinal(core: StreamCore, finalScore: number, gameMs: number): void {
  if (core.final !== null) return;
  core.final = {
    score: whole(finalScore, 0, Number.MAX_SAFE_INTEGER),
    gameMs: Math.max(0, gameMs),
  };
}

/** Send the oldest waiting event frame. False when none waits. */
function sendNextEvent(core: StreamCore, nowMs: number): boolean {
  const next = core.pendingEvents.shift();
  if (next === undefined) return false;
  core.lastSend = nowMs;
  sendFrame(core, { type: "event", seq: nextSeq(core), game_ms: next.gameMs, event: next.event });
  return true;
}

/** Send the final frame. False when the game has not ended. */
function sendFinalFrame(core: StreamCore, nowMs: number): boolean {
  if (core.final === null) return false;
  core.finalSent = true;
  core.lastSend = nowMs;
  sendFrame(core, {
    type: "game_over",
    seq: nextSeq(core),
    game_ms: core.final.gameMs,
    final_score: core.final.score,
  });
  return true;
}

/** Send a state frame when the status changed or the heartbeat is due. */
function sendStateFrame(
  core: StreamCore,
  nowMs: number,
  status: GameStatus,
  paused: boolean,
): void {
  const wire = toFeaturedStatus(status);
  const key = statusKey(wire);
  // A paused game sends the same status again, only as a slow heartbeat.
  const quiet = paused || key === core.lastKey;
  const wait = paused ? PAUSED_HEARTBEAT_MS : HEARTBEAT_MS;
  if (quiet && nowMs - core.lastSend < wait) return;
  core.lastKey = key;
  core.lastSend = nowMs;
  sendFrame(core, {
    type: "state",
    seq: nextSeq(core),
    game_ms: Math.max(0, Number.isFinite(status.elapsedMs) ? status.elapsedMs : 0),
    status: wire,
  });
}

function pumpStream(core: StreamCore, nowMs: number, status: GameStatus, paused: boolean): void {
  if (core.state !== "ready" || core.finalSent || nowMs - core.lastSend < FRAME_GAP_MS) return;
  if (sendNextEvent(core, nowMs) || sendFinalFrame(core, nowMs)) return;
  sendStateFrame(core, nowMs, status, paused);
}

function closeStream(core: StreamCore): void {
  if (core.state === "closed") return;
  core.state = "closed";
  core.socket.close(1000, "client closed");
}

/**
 * Open a stream on a socket. The socket may still be connecting. The auth frame goes out
 * first, as soon as the socket is open.
 */
export function openFeaturedStream(
  socket: SocketLike,
  ticket: string,
  handlers: StreamHandlers,
  options: StreamOptions = {},
): FeaturedStream {
  const core: StreamCore = {
    socket,
    ticket,
    handlers,
    state: "connecting",
    seq: options.startSeq ?? 0,
    lastSend: Number.NEGATIVE_INFINITY,
    lastKey: "",
    pendingEvents: [],
    final: null,
    finalSent: false,
  };
  bindSocket(core);
  return {
    get state() {
      return core.state;
    },
    get lastSeq() {
      return core.seq;
    },
    queueEvent: (ev, gameMs) => queueStreamEvent(core, ev, gameMs),
    finish: (finalScore, gameMs) => setFinal(core, finalScore, gameMs),
    pump: (nowMs, status, paused = false) => pumpStream(core, nowMs, status, paused),
    close: () => closeStream(core),
  };
}

/** The WebSocket URL of the featured route for a page location. */
export function featuredSocketUrl(loc: {
  readonly protocol: string;
  readonly host: string;
}): string {
  const scheme = loc.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${loc.host}${ROUTES.featuredWs}`;
}
